import React, { useState } from 'react';
import { SuperAiWorkspaceModal } from '@/features/super-ai';
import {
  canDownload,
  executeDownload,
  FILE_NOT_LINKED_TITLE,
} from '@/lib/download';
import { CloseCrossIcon, DownloadFileIcon } from './DoctoralIcons';

interface ModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
}

interface HistoryModalProps extends ModalBaseProps {
  title?: string;
  historyLogs?: Array<{ id: string; action: string; details: string; created_at?: string }>;
}

export const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  title = 'История изменений',
  historyLogs = [],
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#071927] border border-cyan-500/40 rounded-xl shadow-[0_0_35px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-[#061421]">
          <div>
            <h3 className="text-base font-semibold text-white tracking-wide">{title}</h3>
            <p className="text-xs text-slate-400 mt-0.5">История действий в текущем рабочем пространстве.</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-cyan-900/30 transition-colors"
          >
            <CloseCrossIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {historyLogs.length === 0 ? (
            <div className="border border-dashed border-cyan-500/20 rounded-lg p-8 text-center text-sm text-slate-400">
              Изменений пока нет.
            </div>
          ) : (
            <div className="space-y-3">
              {historyLogs.map((log) => (
                <div key={log.id} className="p-3 bg-cyan-950/20 border border-cyan-500/20 rounded-lg text-xs text-slate-300">
                  <div className="flex justify-between items-center text-cyan-400 font-semibold mb-1">
                    <span>{log.action}</span>
                    {log.created_at && <span className="text-slate-500 text-[10px]">{new Date(log.created_at).toLocaleString()}</span>}
                  </div>
                  <div>{log.details}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface AdvisorSampleModalProps extends ModalBaseProps {
  docTitle: string;
  sampleUrl?: string | null;
  fileId?: string | null;
}

export const AdvisorSampleModal: React.FC<AdvisorSampleModalProps> = ({
  isOpen,
  onClose,
  docTitle,
  sampleUrl,
  fileId,
}) => {
  if (!isOpen) return null;

  const sampleFields = { file_id: fileId, file_url: sampleUrl };
  const sampleAvailable = canDownload(sampleFields);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl bg-[#071927] border border-cyan-500/40 rounded-xl shadow-[0_0_35px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-[#061421]">
          <div>
            <h3 className="text-base font-semibold text-white tracking-wide">Образец документа</h3>
            <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{docTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-cyan-900/30 transition-colors"
          >
            <CloseCrossIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="p-4 rounded-lg bg-cyan-950/20 border border-cyan-500/20 text-xs text-slate-300 leading-relaxed">
            <span className="font-semibold text-amber-400">Модель работы:</span> Advisor загружает и обновляет официальный рабочий образец. Докторант видит здесь только последнюю актуальную версию, может открыть её, скачать и затем загрузить свой заполненный файл.
          </div>

          <div className="p-4 rounded-lg bg-[#061421] border border-cyan-500/30 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-white">Текущий образец</div>
              <div className="text-[11px] text-slate-400">Для этого документа предусмотрен официальный рабочий шаблон ENU.</div>
            </div>
            {!sampleAvailable ? (
              <p className="text-[11px] text-slate-400 text-right max-w-[200px]">
                Образец не привязан к файлу
              </p>
            ) : null}
            <button
              type="button"
              disabled={!sampleAvailable}
              title={sampleAvailable ? 'Скачать образец' : FILE_NOT_LINKED_TITLE}
              aria-label={sampleAvailable ? 'Скачать образец' : FILE_NOT_LINKED_TITLE}
              onClick={() => void executeDownload(sampleFields, docTitle)}
              className="px-4 py-1.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed font-semibold text-xs tracking-wider uppercase transition flex items-center gap-1.5"
            >
              <DownloadFileIcon className="w-3.5 h-3.5" />
              {sampleAvailable ? 'Скачать' : 'Недоступен'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface AddPublicationModalProps extends ModalBaseProps {
  onAdd: (pub: { title: string; journal: string; doi?: string; indexing: string }) => void;
}

export const AddPublicationModal: React.FC<AddPublicationModalProps> = ({
  isOpen,
  onClose,
  onAdd,
}) => {
  const [title, setTitle] = useState('');
  const [journal, setJournal] = useState('');
  const [doi, setDoi] = useState('');
  const [indexing, setIndexing] = useState('Scopus');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !journal.trim()) return;
    onAdd({ title: title.trim(), journal: journal.trim(), doi: doi.trim() || undefined, indexing });
    setTitle('');
    setJournal('');
    setDoi('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#071927] border border-cyan-500/40 rounded-xl shadow-[0_0_35px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-[#061421]">
          <div>
            <h3 className="text-base font-semibold text-white tracking-wide">Добавить публикацию</h3>
            <p className="text-xs text-slate-400 mt-0.5">Единая сущность публикации — одна запись может иметь несколько индексов.</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-cyan-900/30 transition-colors"
          >
            <CloseCrossIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Название *</label>
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Введите полное название статьи..."
              className="w-full bg-[#030d16] border border-cyan-500/30 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">Журнал / издание *</label>
              <input
                type="text"
                required
                value={journal}
                onChange={(e) => setJournal(e.target.value)}
                placeholder="Название журнала"
                className="w-full bg-[#030d16] border border-cyan-500/30 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">DOI</label>
              <input
                type="text"
                value={doi}
                onChange={(e) => setDoi(e.target.value)}
                placeholder="10.xxxx/..."
                className="w-full bg-[#030d16] border border-cyan-500/30 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Индексация</label>
            <select
              value={indexing}
              onChange={(e) => setIndexing(e.target.value)}
              className="w-full bg-[#030d16] border border-cyan-500/30 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="Scopus">Scopus</option>
              <option value="Web of Science">Web of Science</option>
              <option value="Комитет">Перечень Комитета (КОКСНВО)</option>
              <option value="Конференция">Материалы конференции</option>
              <option value="Другие">Другие научные издания</option>
            </select>
          </div>

          <div className="pt-2 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white"
            >
              Отмена
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-xs transition-colors shadow-[0_0_15px_rgba(6,182,212,0.3)]"
            >
              Сохранить запись
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface SuperAiModalProps extends ModalBaseProps {
  /** @deprecated Super AI workspace uses canonical API; kept for legacy call sites. */
  onQuery?: (prompt: string) => Promise<{ answer: string; suggestions: string[] }>;
}

/** Thin wrapper — canonical Super AI workspace for doctoral students. */
export const DoctoralSuperAiModal: React.FC<SuperAiModalProps> = ({ isOpen, onClose }) => (
  <SuperAiWorkspaceModal isOpen={isOpen} onClose={onClose} activeRole="DOCTORAL_STUDENT" />
);

interface GenericCardModalProps extends ModalBaseProps {
  cardNum: string;
  title: string;
  desc: string;
}

export const GenericCardModal: React.FC<GenericCardModalProps> = ({
  isOpen,
  onClose,
  cardNum,
  title,
  desc,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#071927] border border-cyan-500/40 rounded-xl shadow-[0_0_35px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-[#061421]">
          <div className="flex items-center gap-2.5">
            <span className="px-2 py-0.5 rounded bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-[11px] font-mono">
              {cardNum}
            </span>
            <h3 className="text-base font-semibold text-white tracking-wide">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-cyan-900/30 transition"
          >
            <CloseCrossIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-300 leading-relaxed">{desc}</p>
          <div className="p-4 bg-cyan-950/20 border border-cyan-500/20 rounded-lg text-xs text-slate-400">
            Общий shared-модуль платформы. Все операции синхронизируются с общеуниверситетским реестром и календарем.
          </div>
          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-xs tracking-wider uppercase transition"
            >
              Закрыть
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ProblemReportModalProps extends ModalBaseProps {
  onSubmit: (data: { topic: string; message: string }) => void;
}

export const ProblemReportModal: React.FC<ProblemReportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [topic, setTopic] = useState('Задержка публикации');
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    onSubmit({ topic, message });
    setMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#071927] border border-cyan-500/40 rounded-xl shadow-[0_0_35px_rgba(6,182,212,0.25)] flex flex-col overflow-hidden animate-fadeIn">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cyan-500/20 bg-[#061421]">
          <div>
            <h3 className="text-base font-semibold text-white tracking-wide">Сообщить о проблеме Advisor</h3>
            <p className="text-xs text-slate-400 mt-0.5">Оперативная связь с эдвайзером по рискам и срокам</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-cyan-900/30 transition">
            <CloseCrossIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Тема обращения</label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full bg-[#030d16] border border-cyan-500/30 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400"
            >
              <option value="Задержка публикации">Задержка публикации</option>
              <option value="Задержка утверждения темы">Задержка утверждения темы</option>
              <option value="Вопрос по зарубежной стажировке">Вопрос по зарубежной стажировке</option>
              <option value="Документы практики">Документы практики</option>
              <option value="Подготовка к предзащите">Подготовка к предзащите</option>
              <option value="Другое">Другое</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Описание проблемы *</label>
            <textarea
              required
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Подробно опишите возникшую трудность или вопрос..."
              className="w-full bg-[#030d16] border border-cyan-500/30 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold text-slate-300 hover:text-white">
              Отмена
            </button>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs tracking-wider uppercase transition shadow-[0_0_15px_rgba(245,158,11,0.3)]"
            >
              Отправить
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
